import React, { useEffect, useState, useRef } from "react";
import { pick } from "../utils/fieldExtractors";
import SearchableSelect from "./SearchableSelect";

export default function ProjectDetailsEditor({
  project,
  warrantyStart,
  ownerName = "",
  ownerId = "",
  owners = [],
  configTypes = [],
  defaultConfigTypeId = "",
  siteName = "",
  siteId = "",
  configurationNameDefault = "",
  roomNameDefault = undefined,
  roomNumberDefault = undefined,
  onChange,
  onCreate = undefined,
  canCreate = false,
  createStatus = "idle",
  createError = "",
  createdConfig = null,
}) {
  const [localWarrantyStart, setLocalWarrantyStart] = useState(warrantyStart || "");
  const [roomName, setRoomName] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [installationOwnerId, setInstallationOwnerId] = useState(ownerId || "");
  const [configTypeId, setConfigTypeId] = useState(String(defaultConfigTypeId || ""));
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [localSiteId, setLocalSiteId] = useState(siteId || "");
  const [configurationName, setConfigurationName] = useState(configurationNameDefault || "");
  // Ref for hidden datetime picker
  const dateTimePickerRef = useRef(null);

  function formatDateTimeLocalToZulu(dt) {
    if (!dt) return "";
    // dt is expected in format YYYY-MM-DDTHH:MM from datetime-local (local time)
    // Convert to ISO string in UTC without milliseconds: YYYY-MM-DDTHH:MM:SSZ
    const d = new Date(dt);
    if (isNaN(d.getTime())) return "";
    const iso = d.toISOString(); // e.g. 2025-10-06T00:00:00.000Z
    return iso.slice(0, 19) + "Z"; // strip milliseconds
  }

  function normalizeToZulu(input) {
    if (!input) return "";
    const s = String(input).trim();
    // If already looks like Z-format with seconds, keep as-is
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(s)) return s;
    // If looks like ISO with millis, strip them
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(s)) return s.slice(0, 19) + "Z";
    // If looks like datetime-local without seconds
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return formatDateTimeLocalToZulu(s);
    // Try Date parse fallback
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const iso = d.toISOString();
      return iso.slice(0, 19) + "Z";
    }
    return s; // unknown format; keep original
  }

  useEffect(() => {
    setLocalWarrantyStart(normalizeToZulu(warrantyStart || ""));
  }, [warrantyStart]);

  useEffect(() => {
    setInstallationOwnerId(ownerId || "");
  }, [ownerId]);

  useEffect(() => {
    setLocalSiteId(siteId || "");
  }, [siteId]);

  // Apply defaults for room name/number when provided by parent (e.g., on ticket change or attach success)
  useEffect(() => {
    if (roomNameDefault !== undefined) setRoomName(String(roomNameDefault || ""));
  }, [roomNameDefault]);
  useEffect(() => {
    if (roomNumberDefault !== undefined) setRoomNumber(String(roomNumberDefault || ""));
  }, [roomNumberDefault]);

  // Initialize company id/name from project when available
  const lastProjectIdRef = useRef("");
  useEffect(() => {
    if (!project) return;
    const projId = pick(project, "Id") ?? pick(project, "id") ?? "";
    const cId = pick(project, "Company.Id") ?? pick(project, "company.id") ?? "";
    const cName = pick(project, "Company.Name") ?? pick(project, "company.name") ?? "";
    setCompanyId(String(cId || ""));
    setCompanyName(String(cName || ""));
    const projName = pick(project, "Name") ?? pick(project, "name") ?? "";
    const derived = configurationNameDefault || projName;
    // Reset configuration name whenever the project id changes
    if (String(lastProjectIdRef.current) !== String(projId)) {
      setConfigurationName(String(derived || ""));
      lastProjectIdRef.current = String(projId);
    }
  }, [project, configurationNameDefault]);

  useEffect(() => {
    onChange && onChange({
      warrantyStart: localWarrantyStart,
      roomName,
      roomNumber,
      installationOwnerId,
      configTypeId,
      companyId,
      companyName,
      siteId: localSiteId,
      configurationName,
    });
  }, [localWarrantyStart, roomName, roomNumber, installationOwnerId, configTypeId, companyId, companyName, localSiteId, configurationName, onChange]);

  if (!project) return null;
  const projectId = pick(project, "Id") ?? pick(project, "id") ?? "";

  return (
    <div className="st-card" style={{ marginTop: 24 }}>
      <div className="st-card-title">Project Details</div>
      <form className="st-form" onSubmit={(e) => e.preventDefault()}>
        <div className="st-field">
          <label htmlFor="projId" className="st-label">Project ID</label>
          <input id="projId" className="st-input" value={projectId} readOnly />
        </div>
        <div className="st-field">
          <label className="st-label">Configuration Name</label>
          <input
            className="st-input"
            value={configurationName}
            onChange={(e) => setConfigurationName(e.target.value)}
            placeholder="Configuration name"
          />
        </div>
        <div className="st-field">
          <label htmlFor="roomName" className="st-label">Project Room Name</label>
          <input
            id="roomName"
            className="st-input"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder="Room name"
          />
        </div>
        <div className="st-field">
          <label htmlFor="roomNumber" className="st-label">Room Number</label>
          <input
            id="roomNumber"
            className="st-input"
            value={roomNumber}
            onChange={(e) => setRoomNumber(e.target.value)}
            placeholder="Room number"
          />
        </div>
        <div className="st-field">
          <label className="st-label">Installation Owner</label>
          <SearchableSelect
            items={owners}
            value={installationOwnerId}
            onChange={(val) => setInstallationOwnerId(String(val || ""))}
            getId={(x) => x?.id ?? x?.Id}
            getLabel={(x) => x?.name ?? x?.Name}
            placeholder="Search members..."
          />
        </div>
        <div className="st-field">
          <label htmlFor="warrantyStart" className="st-label">Installation Date</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              id="warrantyStart"
              className="st-input"
              value={localWarrantyStart}
              readOnly
              placeholder="Select installation date and time"
            />
            <button
              type="button"
              className="st-button"
              onClick={() => {
                if (dateTimePickerRef.current) {
                  dateTimePickerRef.current.showPicker?.();
                  // Fallback for browsers without showPicker
                  if (!dateTimePickerRef.current.showPicker) {
                    dateTimePickerRef.current.click();
                  }
                }
              }}
            >
              Calendar
            </button>
            {/* Hidden native datetime-local input to leverage browser picker */}
            <input
              ref={dateTimePickerRef}
              type="datetime-local"
              style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}
              onChange={(e) => {
                const val = e.target.value;
                const formatted = formatDateTimeLocalToZulu(val);
                setLocalWarrantyStart(formatted);
              }}
            />
          </div>
        </div>
        <div className="st-field">
          <label className="st-label">Company Name</label>
          <input
            className="st-input"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Company name"
          />
        </div>
        <div className="st-field">
          <label className="st-label">Site Name</label>
          <input className="st-input" value={siteName} readOnly placeholder="Site name" />
        </div>
        <div className="st-field">
          <label className="st-label">Configuration Type</label>
          <SearchableSelect
            items={configTypes}
            value={configTypeId}
            onChange={setConfigTypeId}
            getId={(x) => x?.Id ?? x?.id}
            getLabel={(x) => x?.Name ?? x?.name}
            placeholder="Search configuration types..."
          />
        </div>
      </form>
      <div className="st-field" style={{ gridColumn: "1 / -1", marginTop: 8 }}>
        <button
          type="button"
          className="st-button"
          disabled={!canCreate || createStatus === "posting"}
          onClick={onCreate}
        >
          {createStatus === "posting" ? "Creating..." : "Create"}
        </button>
        {createStatus === "error" && (
          <div className="st-error" style={{ marginTop: 8 }}>{createError}</div>
        )}
        {createStatus === "success" && (
          <div className="st-success" style={{ marginTop: 8 }}>
            Created successfully{createdConfig?.id ? ` (ID: ${createdConfig.id})` : ""}.
          </div>
        )}
      </div>
    </div>
  );
}
